import { StyleSheet, Text, View } from "@react-pdf/renderer";

import eastviewLogo from "../../../../../assets/logos/eastview-logo.png";
import lendmarqLogoWhite from "../../../../../assets/logos/lendmarq-logo-white.png";
import { useEnv } from "../../../../../hooks/useEnv";
import colors from "../../../../../theme/colors";
import PdfImage from "../../components/images/PdfImage";

/**
 * The brand lockup that sits on a PDF's full-bleed header band, plus the color
 * that band is painted in — see PdfPageBrandBar, the only consumer.
 *
 * PARTNER-SPECIFIC BY DESIGN. A colored bar only works for a partner whose mark
 * reads white-on-brand; Resfin / Resilender / Handsome Homebuyer marks are dark
 * on light, so `usePdfBrandBar()` returns null for them and the page falls back
 * to its plain title-left / mark-right header.
 *
 * - **Eastview** — teal band. `eastview-logo.png` is a white "EASTVIEW" wordmark
 *   already matted onto its own teal, so the band has to be painted in the exact
 *   matte (`colors.logoMatte`) or the composite shows a seam.
 * - **Lendmarq** — blue band, matching the app header. The shipped
 *   `lendmarq-logo.png` is BLUE on transparent and would vanish on it, so the
 *   bar uses `lendmarq-logo-white.png` — the same artwork recolored white, alpha
 *   untouched. Transparent, so there's no matte to match.
 *
 * NOTE FOR THE SIBLING REPOS (lendmarq-los / tpo-portal-frontend): this file is
 * partner-conditional, not Eastview-only — it is meant to be copied ACROSS as
 * is. The two band colors below are deliberately literal so the file doesn't
 * depend on a theme token a sibling's `colors.js` may not export (Eastview's
 * matte falls back to its literal; `LENDMARQ_BAR` is `colors.brand` in
 * lendmarq-los).
 */

/** #1C97C1 — baked into eastview-logo.png. Must match the band exactly. */
const EASTVIEW_MATTE = colors.logoMatte || "#1C97C1";
/** Lendmarq's app-header blue (brand-500 / `colors.brand` in lendmarq-los). */
const LENDMARQ_BAR = "#346BFB";

/**
 * The band color for the current partner, or null when this partner has no
 * branded band (the caller then renders its plain header).
 *
 * ORDER MATTERS, same trap as PdfBuilderLogo: `useEnv().isResfin` is true for
 * resfin AND eastview AND lendmarq, so the specific brands are tested first.
 */
export const usePdfBrandBar = () => {
  const { isEastview, isLendmarq } = useEnv();
  if (isEastview) return { color: EASTVIEW_MATTE };
  if (isLendmarq) return { color: LENDMARQ_BAR };
  return null;
};

/* ---------------------------------------------------------------- Eastview */

/**
 * WHY THE EASTVIEW LOCKUP ISN'T JUST THE IMAGE + A LINE OF TEXT:
 * eastview-logo.png is a 180x50 canvas with the wordmark sitting in the MIDDLE
 * of it — the glyphs only occupy x 22-157, y 18-33, so a third of the image is
 * matte padding below the wordmark. Dropping a `<Text>` after the image
 * therefore starts "INVESTMENT PARTNERS" ~17 asset-px below the wordmark's
 * baseline, hard against the left page padding rather than under the "E", and
 * (at the old fixed `letterSpacing: 1.6`) running ~30% wider than the wordmark
 * itself. That is the loose, misaligned lockup this replaces.
 *
 * So the tagline is positioned off the asset's MEASURED geometry: pulled up to
 * a fixed optical gap under the wordmark, inset to the wordmark's own left edge,
 * and letter-spaced to exactly the wordmark's width. Everything derives from
 * `EV` — if the logo is re-exported, re-measure the glyph box and only these
 * constants change.
 */
const EV = {
  width: 180,
  height: 50,
  markLeft: 22,
  markRight: 157,
  markTop: 18,
  markBottom: 33,
};
const EV_MARK_W = EV.markRight - EV.markLeft; // 135
const EV_CAP_H = EV.markBottom - EV.markTop; // 15

const TAGLINE = "INVESTMENT PARTNERS";
/**
 * Sum of the Helvetica advance widths of TAGLINE, in em — used to solve the
 * letter-spacing that makes the tagline exactly as wide as the wordmark.
 */
const TAGLINE_EM = 12.168;

// Tagline size and the optical gap under the wordmark, both as a fraction of
// the wordmark's cap height so the lockup scales as one piece.
const TAGLINE_SIZE_RATIO = 0.55;
const TAGLINE_GAP_RATIO = 0.45;
// Helvetica sets the cap top roughly a quarter of an em below the line box top;
// the gap is measured cap-to-cap, so back that out of the offset.
const CAP_TOP_INSET_RATIO = 0.25;

const styles = StyleSheet.create({
  tagline: {
    color: "rgba(255,255,255,0.92)",
  },
});

const EastviewLockup = ({ width }) => {
  const scale = width / EV.width;
  const fontSize = EV_CAP_H * TAGLINE_SIZE_RATIO * scale;
  const letterSpacing =
    (EV_MARK_W * scale - TAGLINE_EM * fontSize) / TAGLINE.length;

  return (
    <View>
      <PdfImage src={eastviewLogo} width={width} height={EV.height * scale} />
      <Text
        style={[
          styles.tagline,
          {
            fontSize,
            letterSpacing,
            marginLeft: EV.markLeft * scale,
            // Climb back out of the asset's bottom padding, then down by the
            // optical gap (less the font's own cap inset).
            marginTop:
              (EV.markBottom - EV.height + EV_CAP_H * TAGLINE_GAP_RATIO) *
                scale -
              fontSize * CAP_TOP_INSET_RATIO,
          },
        ]}
      >
        {TAGLINE}
      </Text>
    </View>
  );
};

/* ---------------------------------------------------------------- Lendmarq */

// lendmarq-logo-white.png is the full lockup on its own — one line, no tagline
// to place. 1121x207 native; hold the ratio so the wordmark doesn't stretch.
const LQ_RATIO = 1121 / 207;
// Narrower than Eastview's on purpose: Eastview's is a two-line lockup, so
// matching total heights (rather than widths) is what balances the two bars.
const LQ_WIDTH_RATIO = 96 / 104;

const LendmarqLockup = ({ width }) => {
  const logoWidth = width * LQ_WIDTH_RATIO;
  return (
    <View>
      <PdfImage
        src={lendmarqLogoWhite}
        width={logoWidth}
        height={logoWidth / LQ_RATIO}
      />
    </View>
  );
};

const PdfBrandBarLockup = ({ width = 104 }) => {
  const { isEastview, isLendmarq } = useEnv();
  if (isEastview) return <EastviewLockup width={width} />;
  if (isLendmarq) return <LendmarqLockup width={width} />;
  return null;
};

export default PdfBrandBarLockup;
